import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';
import { AffiliateProvider } from './contexts/AffiliateContext';
import { Toaster } from './components/ui/sonner';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import WishlistPage from './pages/WishlistPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import BlogsPage from './pages/BlogsPage';
import BlogDetailPage from './pages/BlogDetailPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ContentPage from './pages/ContentPage';
import GuestCheckoutPage from './pages/GuestCheckoutPage';
import BulkPurchasePage from './pages/BulkPurchasePage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminCMS from './pages/admin/AdminCMS';
import AdminInvoiceSettings from './pages/admin/AdminInvoiceSettings';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminSettings from './pages/admin/AdminSettings';
import AdminCourierRules from './pages/admin/AdminCourierRules';
import AdminSubscriptions from './pages/admin/AdminSubscriptions';
import AdminRevenue from './pages/admin/AdminRevenue';
import AdminUsers from './pages/admin/AdminUsers';
import AdminGallery from './pages/admin/AdminGallery';
import AdminManualInvoices from './pages/admin/AdminManualInvoices';
import AdminInvoicePrint from './pages/admin/AdminInvoicePrint';
import AdminReturns from './pages/admin/AdminReturns';
import AdminAffiliates from './pages/admin/AdminAffiliates';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import '@/App.css';

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:productId" element={<ProductDetailPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/blogs" element={<BlogsPage />} />
      <Route path="/blogs/:slug" element={<BlogDetailPage />} />
      <Route path="/subscription" element={<SubscriptionPage />} />
      <Route path="/page/:slug" element={<ContentPage />} />
      <Route path="/guest-checkout" element={<GuestCheckoutPage />} />
      <Route path="/bulk-purchase" element={<BulkPurchasePage />} />

      <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
      <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
      <Route path="/order-success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
      <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
      <Route path="/admin/cms" element={<AdminRoute><AdminCMS /></AdminRoute>} />
      <Route path="/admin/invoices" element={<AdminRoute><AdminInvoices /></AdminRoute>} />
      <Route path="/admin/coupons" element={<AdminRoute><AdminCoupons /></AdminRoute>} />
      <Route path="/admin/invoice-settings" element={<AdminRoute><AdminInvoiceSettings /></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
      <Route path="/admin/courier-rules" element={<AdminRoute><AdminCourierRules /></AdminRoute>} />
      <Route path="/admin/subscriptions" element={<AdminRoute><AdminSubscriptions /></AdminRoute>} />
      <Route path="/admin/revenue" element={<AdminRoute><AdminRevenue /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/gallery" element={<AdminRoute><AdminGallery /></AdminRoute>} />
      <Route path="/admin/manual-invoices" element={<AdminRoute><AdminManualInvoices /></AdminRoute>} />
      <Route path="/admin/manual-invoices/print/:invoiceId" element={<AdminRoute><AdminInvoicePrint /></AdminRoute>} />
      <Route path="/admin/returns" element={<AdminRoute><AdminReturns /></AdminRoute>} />
      <Route path="/admin/affiliates" element={<AdminRoute><AdminAffiliates /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SiteSettingsProvider>
        <AuthProvider>
          <AffiliateProvider>
            <AppRouter />
            <Toaster position="top-right" />
          </AffiliateProvider>
        </AuthProvider>
      </SiteSettingsProvider>
    </BrowserRouter>
  );
}

export default App;
